import React, { useEffect, useState } from "react";
import { X, TrendingUp, TrendingDown, Minus } from "lucide-react";
import {
  LineChart, Line, PieChart, Pie, Cell, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, ReferenceLine
} from "recharts";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

function ratingColor(r) {
  const n = parseFloat(r);
  if (n >= 4.5) return "#00ff90";
  if (n >= 3.5) return "#60a5fa";
  if (n >= 2.5) return "#fbbf24";
  return "#f87171";
}

function TrendIcon({ t }) {
  if (t === "up")   return <TrendingUp   size={13} className="text-[#00ff90]" />;
  if (t === "down") return <TrendingDown size={13} className="text-red-400"   />;
  return <Minus size={13} className="text-white/20" />;
}

const GOAL_STATUS_STYLE = {
  completed:   "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  in_progress: "text-blue-400 bg-blue-500/10 border-blue-500/20",
  at_risk:     "text-red-400 bg-red-500/10 border-red-500/20",
  not_started: "text-white/25 bg-white/5 border-white/10",
};

const tooltipStyle = {
  contentStyle: { background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 },
  labelStyle: { color: "#00ff90" },
};

export default function MemberPerformanceDrawer({ member, onClose }) {
  const [snap,    setSnap]    = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!member?.id) return;
    const token = localStorage.getItem("token");
    setLoading(true);
    fetch(`${API_BASE}/api/manager/performance/employee/${member.id}/snapshot`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setSnap(d))
      .catch(() => setSnap(null))
      .finally(() => setLoading(false));
  }, [member?.id]);

  if (!member) return null;

  const ratings     = snap?.recentRatings  || [];
  const goals       = snap?.goals          || [];
  const feedback    = snap?.peerFeedback   || [];
  const competencies = snap?.competencies  || []; // [{subject, score}] for radar

  // Goals donut
  const done        = goals.filter(g => g.status === "completed").length;
  const inProgress  = goals.filter(g => g.status === "in_progress").length;
  const atRisk      = goals.filter(g => g.status === "at_risk").length;
  const notStarted  = goals.filter(g => g.status === "not_started" || !g.status).length;
  const goalsDonut  = [
    { name: "Done",        value: done,       color: "#00ff90" },
    { name: "In Progress", value: inProgress, color: "#60a5fa" },
    { name: "At Risk",     value: atRisk,     color: "#f87171" },
    { name: "Not Started", value: notStarted, color: "rgba(255,255,255,0.15)" },
  ].filter(d => d.value > 0);

  // Rating trend for line chart
  const ratingTrend = ratings.map(r => ({
    period: r.period,
    rating: parseFloat(r.rating),
  }));

  const teamAvg = snap?.teamAvgRating || null;

  return (
    <div className="fixed inset-0 z-[10060]">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="absolute right-0 top-0 h-full w-full sm:w-[480px] bg-[#0d0d0d] border-l border-[#00ff90]/20 shadow-[-20px_0_60px_#00ff9011] text-white flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 shrink-0">
          <div>
            <p className="font-semibold text-white">{member.name}</p>
            <p className="text-xs text-[#00ff90]/50 mt-0.5">{member.role}</p>
          </div>
          <div className="flex items-center gap-3">
            {member.rating != null && (
              <div className="text-right">
                <p className="text-lg font-bold" style={{ color: ratingColor(member.rating) }}>
                  {parseFloat(member.rating).toFixed(1)}
                </p>
                <p className="text-xs text-white/30">Current rating</p>
              </div>
            )}
            <button onClick={onClose}
              className="w-8 h-8 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/40 hover:text-white transition-colors">
              <X size={14} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading ? (
            <div className="space-y-3">{[1,2,3,4].map(i => <div key={i} className="h-24 rounded-xl bg-white/5 animate-pulse" />)}</div>
          ) : (
            <>
              {/* Current rating card */}
              {member.rating != null && (
                <div className="rounded-xl border border-[#00ff90]/15 bg-[#00ff90]/5 p-4 flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl border flex items-center justify-center shrink-0"
                    style={{ borderColor: `${ratingColor(member.rating)}40`, background: `${ratingColor(member.rating)}10` }}>
                    <span className="text-2xl font-bold" style={{ color: ratingColor(member.rating) }}>
                      {parseFloat(member.rating).toFixed(1)}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${
                        member.status === "AT_RISK"
                          ? "text-red-400 bg-red-500/10 border-red-500/20"
                          : "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20"
                      }`}>{member.status === "AT_RISK" ? "At Risk" : "On Track"}</span>
                      {member.trend && <TrendIcon t={member.trend} />}
                    </div>
                    {teamAvg && (
                      <p className="text-xs text-white/30">
                        Team avg: <span style={{ color: ratingColor(teamAvg) }}>{parseFloat(teamAvg).toFixed(1)}</span>
                        {" · "}
                        <span style={{ color: member.rating >= teamAvg ? "#00ff90" : "#f87171" }}>
                          {member.rating >= teamAvg ? "↑ Above" : "↓ Below"} avg
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Rating trend line chart */}
              {ratingTrend.length > 1 && (
                <div className="rounded-xl border border-white/5 bg-white/3 p-4">
                  <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">Rating Trend</p>
                  <ResponsiveContainer width="100%" height={110}>
                    <LineChart data={ratingTrend} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                      <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                      <XAxis dataKey="period" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} />
                      <YAxis domain={[0, 5]} tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} />
                      <Tooltip {...tooltipStyle} formatter={v => [`${v.toFixed(1)} / 5`, "Rating"]} />
                      {teamAvg && (
                        <ReferenceLine y={teamAvg} stroke="rgba(255,255,255,0.15)" strokeDasharray="4 4"
                          label={{ value: "Team avg", fill: "rgba(255,255,255,0.2)", fontSize: 9 }} />
                      )}
                      <Line type="monotone" dataKey="rating" stroke="#00ff90" strokeWidth={2}
                        dot={(props) => {
                          const { cx, cy, value } = props;
                          return <circle key={`dot-${cx}`} cx={cx} cy={cy} r={4} fill={ratingColor(value)} stroke="none" />;
                        }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Goals donut + list */}
              {goals.length > 0 && (
                <div className="rounded-xl border border-white/5 bg-white/3 p-4">
                  <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">
                    Goals · {done}/{goals.length} completed
                  </p>
                  <div className="flex items-start gap-4 mb-3">
                    {goalsDonut.length > 0 && (
                      <ResponsiveContainer width={80} height={80}>
                        <PieChart>
                          <Pie data={goalsDonut} cx="50%" cy="50%" innerRadius={22} outerRadius={36} paddingAngle={2} dataKey="value">
                            {goalsDonut.map((d, i) => <Cell key={i} fill={d.color} fillOpacity={0.85} />)}
                          </Pie>
                          <Tooltip {...tooltipStyle} formatter={(v, n) => [v, n]} />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                    <div className="space-y-1.5 flex-1">
                      {goalsDonut.map(d => (
                        <div key={d.name} className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: d.color }} />
                            <span className="text-xs text-white/50">{d.name}</span>
                          </div>
                          <span className="text-xs font-medium" style={{ color: d.color }}>{d.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <ul className="space-y-1.5">
                    {goals.map((g, i) => {
                      const style = GOAL_STATUS_STYLE[g.status] || GOAL_STATUS_STYLE.not_started;
                      return (
                        <li key={i} className="flex items-center justify-between gap-2">
                          <span className={`text-xs ${g.status === "completed" ? "line-through text-white/25" : "text-white/60"} truncate`}>
                            {g.title}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full border capitalize shrink-0 ${style}`}>
                            {(g.status || "").replace("_", " ")}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Competency radar */}
              {competencies.length >= 3 && (
                <div className="rounded-xl border border-white/5 bg-white/3 p-4">
                  <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">Competency Breakdown</p>
                  <ResponsiveContainer width="100%" height={180}>
                    <RadarChart data={competencies}>
                      <PolarGrid stroke="rgba(255,255,255,0.08)" />
                      <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} />
                      <Radar dataKey="score" stroke="#00ff90" fill="#00ff90" fillOpacity={0.15} strokeWidth={2} />
                      <Tooltip {...tooltipStyle} formatter={v => [`${v}/5`, "Score"]} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Peer feedback */}
              {feedback.length > 0 && (
                <div className="rounded-xl border border-white/5 bg-white/3 p-4">
                  <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">Peer Feedback ({feedback.length})</p>
                  <ul className="space-y-2">
                    {feedback.slice(0, 3).map((f, i) => (
                      <li key={i} className="text-xs text-white/50 italic border-l-2 border-[#00ff90]/20 pl-3">"{f.comment}"</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="rounded-xl border border-white/5 bg-white/3 p-3">
                <p className="text-xs text-white/20 text-center">Performance data only — no personal information shown</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}